import {
  SEED_MARKETS,
  type DsarRequest,
  type DsarStatus,
  type Market,
  type User,
} from '../dataProvider/types';
import { deadlinePhase, dueAt, type DeadlinePhase } from './dsarState';

/**
 * The DSAR queue query/aggregation module (D-dsar, the Phase 7 lead) - the ONE place DSARs are
 * shaped for the worklist. Same seam idiom as the tickets / chargebacks / suspensions
 * worklists: v1 aggregates client-side from the mock provider, every selector lives here, and
 * the real-data migration swaps this module for a server-side endpoint without the surface
 * changing.
 *
 * The statutory DEADLINE is DERIVED off the injected clock (requestedAt plus the statutory
 * window, the suspension phaseFor shape), NOT a stored flag and NO enforcement engine. Each
 * row carries the member joined in (name, email) so the package can be assembled from the
 * request context.
 *
 * REGION: OVERSIGHT-GLOBAL with a region FACET (D-dsar). Compliance is cross-region
 * accountability, so the queue spans markets by default (the audit-log / fraud-queue posture,
 * NOT the operational switcher). The optional `regionFilter` narrows by the request's stored
 * market by exact value.
 */

export interface DsarRow {
  id: number;
  subjectUserId: number;
  memberName: string;
  memberEmail: string;
  market: Market;
  status: DsarStatus;
  requestedAt: string;
  /** Derived: the statutory due instant (requestedAt plus the window). */
  dueAt: string;
  fulfilledAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  /** Derived off the injected clock for an unresolved request; null once resolved. */
  deadlinePhase: DeadlinePhase | null;
}

export interface DsarQueueView {
  /** Open work (received + in_progress), urgency-sorted: breached on top, then soonest due. */
  active: DsarRow[];
  /** History (fulfilled + rejected), most recent activity first. */
  resolved: DsarRow[];
}

export interface DsarQueueInput {
  dsarRequests: DsarRequest[];
  users: User[];
}

export interface DsarQueueOptions {
  /** The injected clock the statutory-deadline derivation is measured against. */
  now: number;
  /** Exact-value market facet (uk / us), or null for the global view. */
  regionFilter?: Market | null;
}

const UNRESOLVED: ReadonlySet<DsarStatus> = new Set<DsarStatus>([
  'received',
  'in_progress',
]);

/** Breached first, then soonest statutory due first, then id. */
const urgencyOrder = (a: DsarRow, b: DsarRow): number => {
  const rank = (r: DsarRow): number => (r.deadlinePhase === 'breached' ? 0 : 1);
  return rank(a) - rank(b) || a.dueAt.localeCompare(b.dueAt) || a.id - b.id;
};

/** Resolved is history: most recent fulfil / reject first, then newest request. */
const activityDesc = (r: DsarRow): string =>
  r.fulfilledAt ?? r.rejectedAt ?? r.requestedAt;
const newestFirst = (a: DsarRow, b: DsarRow): number =>
  activityDesc(b).localeCompare(activityDesc(a)) || b.id - a.id;

export function buildDsarQueueView(
  input: DsarQueueInput,
  opts: DsarQueueOptions,
): DsarQueueView {
  const usersById = new Map(input.users.map((u) => [u.id, u]));

  const rows: DsarRow[] = input.dsarRequests
    .filter((r) => SEED_MARKETS.includes(r.market))
    .filter((r) => opts.regionFilter == null || r.market === opts.regionFilter)
    .map((r) => {
      const user = usersById.get(r.subjectUserId);
      const unresolved = UNRESOLVED.has(r.status);
      return {
        id: r.id,
        subjectUserId: r.subjectUserId,
        memberName: user?.name ?? '',
        memberEmail: user?.email ?? '',
        market: r.market,
        status: r.status,
        requestedAt: r.requestedAt,
        dueAt: dueAt(r.requestedAt),
        fulfilledAt: r.fulfilledAt ?? null,
        rejectedAt: r.rejectedAt ?? null,
        rejectionReason: r.rejectionReason ?? null,
        deadlinePhase: unresolved
          ? deadlinePhase(r.requestedAt, opts.now)
          : null,
      };
    });

  return {
    active: rows.filter((r) => UNRESOLVED.has(r.status)).sort(urgencyOrder),
    resolved: rows.filter((r) => !UNRESOLVED.has(r.status)).sort(newestFirst),
  };
}
